import { useEffect, useRef, useContext } from 'react';
import { useApp, AppContext } from '@/context/AppContext';
import { useToast } from '@/hooks/use-toast';
import { PetInventoryItem } from '@/types';
import { Sparkles } from 'lucide-react';

export function PetDropNotifications() {
  const context = useContext(AppContext);
  
  // Return early if context is not available
  if (!context) {
    return null;
  }
  
  const { state, loading } = context;
  const { toast } = useToast();
  const previousInventoryRef = useRef<PetInventoryItem[]>([]);

  useEffect(() => {
    // Don't process notifications while loading
    if (loading || !state.isAuthenticated || !state.petCollection) return;

    const previousInventory = previousInventoryRef.current;
    const currentInventory = state.petCollection.inventory;

    // Only check for new items if we have a previous state to compare against
    if (previousInventory.length > 0) {
      // Find newly added items
      const newItems = currentInventory.filter(currentItem => {
        const prevItem = previousInventory.find(prev => 
          prev.item_type === currentItem.item_type && 
          prev.item_key === currentItem.item_key
        );
        return !prevItem || currentItem.quantity > prevItem.quantity;
      });

      // Show notification for newly dropped items
      if (newItems.length > 0) {
        const dropMessages = newItems.map(item => {
          const prevItem = previousInventory.find(prev => 
            prev.item_type === item.item_type && 
            prev.item_key === item.item_key
          );
          const quantityGained = item.quantity - (prevItem?.quantity || 0);
          
          // Get item display info
          const getItemIcon = (type: string, key: string) => {
            if (type === 'egg') {
              const eggIcons: Record<string, string> = {
                wolf: '🐺🥚',
                tiger: '🐅🥚',
                panda: '🐼🥚',
                fox: '🦊🥚',
                dragon: '🐉🥚',
                phoenix: '🔥🥚',
                unicorn: '🦄🥚',
                griffin: '🦅🥚',
                hydra: '🐲🥚',
                cerberus: '🐕🥚'
              };
              return eggIcons[key] || '🥚';
            }
            if (type === 'potion') {
              const potionIcons: Record<string, string> = {
                base: '🧪',
                white: '⚪🧪',
                desert: '🏜️🧪',
                red: '🔴🧪',
                shade: '🌚🧪',
                golden: '🌟🧪',
                zombie: '🧟🧪',
                skeleton: '💀🧪',
                cotton_candy_pink: '🍭🧪',
                cotton_candy_blue: '💙🧪'
              };
              return potionIcons[key] || '🧪';
            }
            if (type === 'food') {
              const foodIcons: Record<string, string> = {
                basic_seed: '🌱',
                premium_berry: '🍓',
                energy_nectar: '🍯',
                golden_apple: '🍎',
                magical_treat: '✨',
                legendary_feast: '🍖'
              };
              return foodIcons[key] || '🍽️';
            }
            return '🎁';
          };

          const getItemName = (type: string, key: string) => {
            if (type === 'egg') {
              return `${key.charAt(0).toUpperCase() + key.slice(1)} Egg`;
            }
            if (type === 'potion') {
              return `${key.charAt(0).toUpperCase() + key.slice(1)} Hatching Potion`;
            }
            if (type === 'food') {
              const foodNames: Record<string, string> = {
                basic_seed: 'Basic Seed',
                premium_berry: 'Premium Berry',
                energy_nectar: 'Energy Nectar',
                golden_apple: 'Golden Apple',
                magical_treat: 'Magical Treat',
                legendary_feast: 'Legendary Feast'
              };
              return foodNames[key] || key;
            }
            return key;
          };

          return {
            icon: getItemIcon(item.item_type, item.item_key),
            name: getItemName(item.item_type, item.item_key),
            quantity: quantityGained,
            type: item.item_type
          };
        });

        if (dropMessages.length > 0) {
          toast({
            title: "🎉 Pet Items Dropped!",
            description: (
              <div className="space-y-2">
                <p className="text-sm font-medium">Activity completed! You received:</p>
                <div className="space-y-1">
                  {dropMessages.map((drop, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <Sparkles className="w-3 h-3 text-yellow-500" />
                      <span>{drop.icon}</span>
                      <span>{drop.name}</span>
                      {drop.quantity > 1 && <span className="text-muted-foreground">x{drop.quantity}</span>}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Check your Pet Collection to use these items!
                </p>
              </div>
            ),
            duration: 5000,
          });
        }
      }
    }

    // Update the previous inventory reference
    previousInventoryRef.current = [...currentInventory];
  }, [state.petCollection?.inventory, loading, state.isAuthenticated, toast]);

  return null; // This component doesn't render anything
}